import { type RequiredKeys } from 'utility-types';

import { type ExtractValues, type Prettify } from '../types';

/**
 * Returns a new object, {@link Omit<T, K>}, with the provided keys, {@link K[]}, excluded.
 *
 * This method copies the own, enumerable string-keyed properties of the provided object,
 * {@link T}, that are not included in the provided keys, {@link K[]} - with strict typing around
 * those keys ensuring that they are in fact in the provided object, {@link T}.
 *
 * @param {T} obj The object for which the provided keys, {@link K[]}, should be omitted.
 * @param {[K, ...K[]] | K} firstKey
 *   Either an array of keys, {@link K[]}, or a single key, {@link K}, in the case that the keys are
 *   being provided as arguments.
 * @param {K[]}keys
 *   Additional keys that should be removed from the object, {@link T}, in the case that the keys
 *   are being provided as arguments.
 *
 * @example
 * // Providing the keys as an array.  Returns { c: 'c' }
 * omit({ a: 'a', b: 'b', c: 'c' }, ['a', 'b']);
 * @example
 * // Providing the keys as arguments.  Returns { c: 'c' }
 * omit({ a: 'a', b: 'b', c: 'c' }, 'a', 'b');
 *
 * @returns {Omit<T, K>} The object, {@link T}, with the keys, {@link K[]}, excluded.
 *
 * @template {T}
 *   Represents the provided object.
 * @template {K}
 *   Represents keys of the provided object, {@link T}.
 */
export const omit = <T extends object, K extends keyof T>(
  obj: T,
  firstKey: [K, ...K[]] | K,
  ...keys: K[]
): Omit<T, K> => {
  const keysToOmit: (keyof T)[] = Array.isArray(firstKey)
    ? [...firstKey, ...keys]
    : [firstKey, ...keys];
  return extractKeys(obj).reduce(
    (acc, key) => (keysToOmit.includes(key) ? acc : Object.assign(acc, { [key]: obj[key] })),
    {} as Omit<T, K>,
  );
};

/**
 * Represents the keys that will not be optional in the resulting pruned value of the object,
 * {@link T}, that is being pruned.
 *
 * When determining the keys that will be required in the resulting pruned object, it is important
 * that we exclude the keys of {@link T} that are associated with values that represent the values
 * being pruned, {@link V}, only.
 *
 * For instance, if we have an object {@link T} represented by
 * { a: undefined; b?: string, c: string }, and {@link V} is 'undefined', then the required keys
 * are only 'c' (not 'a' | 'c') - because 'a' will be excluded from the pruned object since it can
 * only be 'undefined' and 'b' is already an optional key.
 *
 * @example
 * // "a"
 * ReqKeysToPrune<{ a: string | undefined; b?: string; c: null }, undefined | null>
 */
type ReqKeysToPrune<T extends Record<string, unknown>, V extends T[keyof T]> = ExtractValues<{
  [key in RequiredKeys<T>]: [Extract<T[key], V>] extends [never] ? key : never;
}>;

/**
 * Represents the keys that will be optional in the resulting pruned value of the object, {@link T},
 * that is being pruned.
 *
 * When determining the keys that will be optional in the resulting pruned object, it is important
 * that we include the keys of {@link T} that are associated with values that represent the values
 * being pruned, {@link V}, only.
 *
 * For instance, if we have an object {@link T} represented by
 * { a: undefined; b?: string, c: string }, and {@link V} is 'undefined', then the optional keys
 * are 'a' | 'b' (and not just 'b') - because 'a' will be excluded from the pruned object since it
 * can only be 'undefined'.
 *
 * @example
 * // "a" | "b"
 * OptKeysToPrune<{ a: undefined; b?: string, c: string }, undefined>
 */
type OptKeysToPrune<T extends Record<string, unknown>, V extends T[keyof T]> = ExtractValues<{
  [key in Exclude<keyof T, ReqKeysToPrune<T, V>>]: key;
}>;

/**
 * Represents the result of pruning the object {@link T} by removing keys that are associated with
 * values represented by {@link V}.
 */
type PrunedObject<T extends Record<string, unknown>, V extends T[keyof T]> = Prettify<
  {
    [key in OptKeysToPrune<T, V>]?: Exclude<T[key], V>;
  } & {
    [key in ReqKeysToPrune<T, V>]: Exclude<T[key], V>;
  }
>;

/**
 * Removes the provided values, {@link V[]}, from the object, {@link T}, and returns a new object
 * with the keys associated with those values removed.
 *
 * @param {T} obj The object for which the provided values {@link V[]} should be removed.
 * @param {V[]} [valuesToPrune = undefined]
 *   The values that should be removed from the provided object, {@link T}.
 *
 * @returns {PrunedObject<T, V>} The object, {@link T}, with the values, {@link V[]}, removed.
 */
export const pruneObject = <
  T extends Record<string, unknown>,
  V extends T[keyof T] = T[keyof T] & undefined,
>(
  obj: T,
  valuesToPrune?: V[],
): PrunedObject<T, V> => {
  const pruneValues = valuesToPrune ?? ([undefined] as V[]);
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (!pruneValues.includes(value as V)) {
        return { ...acc, [key]: value };
      }
      return acc;
    },
    {} as PrunedObject<T, V>,
  );
};

/**
 * Retrieves the keys of an object in a type-safe way, avoiding the need to use type coercion.
 *
 * The purpose of this method is to avoid unsafe type coercion that comes from situations similar
 * to the following:
 *
 * >>> const obj = { name: 'apple', color: 'red' } as const;
 * >>> (Object.keys(obj) as (keyof typeof obj)[]).map(key: 'name' | 'color' => ... )
 *
 * This is unsafe because the coercion will still satisfy the compiler even if we do
 *
 * >>> const obj2 = { status: 'failed', message: 'There was an error.' } as const;
 * >>> (Object.keys(obj) as (keyof typeof obj2)[]).map(key: 'status' | 'message' => ... )
 *
 * The Object.keys() method returns an array containing only the own, enumerable string-keyed
 * property names of the object - it does not include inherited properties from the prototype chain.
 * On the other hand, for...in iterates over all enumerable string-keyed properties of an object,
 * including those inherited from its prototype chain.  But, the for...in iteration is needed to
 * preserve the types of the object keys without coercion.
 *
 * So, in order for this method to behave the same way as Object.keys(), the 'object.hasOwnProperty'
 * method is used inside of the for...in iteration so its behavior is identical to Object.keys().
 *
 * @param {T extends object} obj
 *   The object for which the keys should be returned.
 *
 * @returns {(keyof T)[]} The keys of the specified object.
 */
export const extractKeys = <T extends object>(obj: T): (keyof T)[] => {
  let keys: (keyof T)[] = [];
  for (const k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) {
      keys = [...keys, k];
    }
  }
  return keys;
};

type Exact<A, B> = Required<Record<keyof A & keyof B, unknown>>;

type InferredObj<Obj> = { [key in keyof Obj]-?: unknown };

/**
 * A utility method that can be used to construct an exhaustive array of all of the keys of an
 * object in a type-safe way.
 *
 * In certain situations, it is useful to construct an array where the elements in the array
 * represent the exhaustive set of keys of an object-type.  This is not something that can be
 * easily typed for alone, because the following does not guarantee that the array is exhaustive:
 *
 * >>> const MyObjKeys = [...] as (keyof typeof obj)
 *
 * Additionally, a readonly array type cannot be used, because it does not guarantee that each key
 * of the object-type is in the array, and it is sensitive to the ordering of the keys in the
 * array.
 *
 * Instead, this method can be used to create an array of the keys of an object type in a way that
 * will cause a compilation error if the keys in the array ever fall out of sync with the
 * object-type.
 *
 * Note that it is important that the generic type {@link R} be explicitly provided to this method.
 * The generic type {@link R} defaults to the never type to ensure that it is provided.  The method
 * has to use function currying for the type inference of the keys to work properly.
 *
 * @example
 * In the below example, the compiler will fail if the type MyComponentProps is ever updated and
 * falls out of sync with MyComponentPropNames.
 *
 * ```ts
 * const MyComponentProps = { readonly id: string; readonly name: string };
 *
 * const MyComponentPropNames = extractExhaustiveKeys<MyComponentProps>()({
 *   id: true,
 *   name: true
 * })
 * ```
 */
export const extractExhaustiveKeys =
  <Obj = never>() =>
  <Inferred extends InferredObj<Obj>>(
    obj: Exact<Inferred, InferredObj<Obj>>,
  ): (keyof Exact<Inferred, InferredObj<Obj>>)[] =>
    extractKeys(obj);
